import React, { useCallback, useRef, useState } from "react";
import CopyrightOutlinedIcon from "@mui/icons-material/CopyrightOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { updateDataset } from "@/lib/api/datasets";
import { TooltipButton } from "@/components/common/TooltipButton";
import { DropdownMenu } from "@/components/common/DropdownMenu";
import { TextInput } from "@/components/common/TextInput";
import { Button } from "@/components/common/Button";
import { DatasetMetaSection, MetaAddButton } from "./DatasetMetaSection";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import {
  DATASET_LICENSES,
  LICENSE_ID_OTHER,
  licenseById,
} from "@/generated-licenses";

const MAX_NAME_LEN = 100;
const MAX_URL_LEN = 2000;

export interface DatasetLicenseValue {
  license: string | null;
  license_id: string | null;
  license_name: string | null;
  license_url: string | null;
}

const EMPTY: DatasetLicenseValue = {
  license: null,
  license_id: null,
  license_name: null,
  license_url: null,
};

/// A known id resolves its URL from the generated table; only "other" carries
/// one on the dataset. Legacy rows have neither and render as plain text.
const urlFor = (v: DatasetLicenseValue): string | null =>
  (v.license_id ? licenseById(v.license_id)?.url : null) ?? v.license_url;

interface DatasetLicenseProps {
  datasetId: string;
  value: DatasetLicenseValue;
  canEdit: boolean;
  onSaved: (value: DatasetLicenseValue) => void;
  pr?: string;
}

export const DatasetLicense: React.FC<DatasetLicenseProps> = ({
  datasetId,
  value,
  canEdit,
  onSaved,
  pr,
}) => {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const addRef = useRef<HTMLDivElement>(null);

  const openMenu = useCallback(() => {
    const isOther = value.license_id === LICENSE_ID_OTHER;
    setCustomName(isOther ? (value.license_name ?? "") : "");
    setCustomUrl(isOther ? (value.license_url ?? "") : "");
    setCustomOpen(isOther);
    setOpen(true);
  }, [value]);

  // Optimistic: reflect the pick immediately, roll back on failure.
  const persist = useCallback(
    async (next: DatasetLicenseValue) => {
      setOpen(false);
      if (
        next.license_id === value.license_id &&
        next.license_url === value.license_url
      )
        return;
      const prev = value;
      onSaved(next);
      try {
        await updateDataset(datasetId, {
          license_id: next.license_id,
          license_name: next.license_name,
          license_url: next.license_url,
        });
      } catch {
        onSaved(prev);
        showNotificationGlobal("Failed to save license", "error");
      }
    },
    [datasetId, value, onSaved],
  );

  const pick = useCallback(
    (id: string) => {
      const l = licenseById(id);
      if (!l) return;
      void persist({
        license: l.label,
        license_id: l.id,
        license_name: null,
        license_url: null,
      });
    },
    [persist],
  );

  const name = customName.trim();
  const url = customUrl.trim();
  const customValid =
    name.length > 0 &&
    name.length <= MAX_NAME_LEN &&
    url.startsWith("https://") &&
    url.length <= MAX_URL_LEN;

  const saveCustom = useCallback(() => {
    if (!customValid) return;
    void persist({
      license: name,
      license_id: LICENSE_ID_OTHER,
      license_name: name,
      license_url: url,
    });
  }, [customValid, name, url, persist]);

  if (!canEdit && !value.license) return null;

  const href = urlFor(value);

  return (
    <>
      <DatasetMetaSection
        icon={<CopyrightOutlinedIcon className="icon-sm" />}
        label="License"
        pr={pr}
        collapsible
        defaultCollapsed
        itemCount={value.license ? 1 : 0}
        action={
          canEdit && !value.license ? (
            <div ref={addRef}>
              <MetaAddButton label="Add a license" onClick={openMenu} />
            </div>
          ) : undefined
        }
      >
        {value.license ? (
          <div ref={addRef} className="relative group/lic">
            <div className="w-full rounded-app border border-transparent px-2 py-1.5 leading-5 text-fg-medium">
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer license"
                  className="inline-flex items-center gap-1 hover:text-accent hover:underline"
                >
                  {value.license}
                  <OpenInNewOutlinedIcon className="icon-xs" />
                </a>
              ) : (
                value.license
              )}
            </div>
            {canEdit && (
              <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 transition-opacity group-hover/lic:opacity-100 focus-within:opacity-100">
                <TooltipButton
                  title="Change license"
                  aria-label="Change license"
                  onClick={open ? () => setOpen(false) : openMenu}
                  variant="default"
                  size="xs"
                >
                  <EditOutlinedIcon />
                </TooltipButton>
                <TooltipButton
                  title="Remove"
                  aria-label="Remove license"
                  onClick={() => void persist(EMPTY)}
                  variant="default"
                  size="xs"
                  className="hover:text-error"
                >
                  <CloseOutlinedIcon />
                </TooltipButton>
              </div>
            )}
          </div>
        ) : null}
      </DatasetMetaSection>

      {canEdit && (
        <DropdownMenu
          anchorRef={addRef}
          isOpen={open}
          onClose={() => setOpen(false)}
          placement="bottom-end"
          style={{ width: "18rem" }}
        >
          <div className="max-h-72 overflow-y-auto p-1">
            {DATASET_LICENSES.map((l) => {
              const selected = value.license_id === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => pick(l.id)}
                  title={l.name}
                  className={`w-full flex items-center px-2 py-1.5 rounded-app text-left text-small cursor-pointer ${
                    selected
                      ? "bg-list-selected-light hover:bg-list-selected"
                      : "hover:bg-list-hover"
                  }`}
                >
                  {l.label}
                </button>
              );
            })}

            <div className="mt-1 pt-1 border-t border-border">
              {customOpen ? (
                <div className="flex flex-col gap-1 p-1">
                  <TextInput
                    value={customName}
                    onChange={setCustomName}
                    placeholder="License name"
                    ariaLabel="Custom license name"
                    maxLength={MAX_NAME_LEN}
                    className="input-custom-sm"
                  />
                  <TextInput
                    value={customUrl}
                    onChange={setCustomUrl}
                    placeholder="https://…"
                    ariaLabel="Custom license URL"
                    maxLength={MAX_URL_LEN}
                    className="input-custom-sm"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        saveCustom();
                      } else if (e.key === "Escape") {
                        setOpen(false);
                      }
                    }}
                  />
                  <Button
                    onClick={saveCustom}
                    disabled={!customValid}
                    variant="primary"
                    size="sm"
                  >
                    Set license
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCustomOpen(true)}
                  className="w-full flex items-center px-2 py-1.5 rounded-app text-left text-small cursor-pointer hover:bg-list-hover"
                >
                  Other…
                </button>
              )}
            </div>
          </div>
        </DropdownMenu>
      )}
    </>
  );
};
